import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const inputCls = "h-11 rounded-xl bg-white/[0.04] border-white/10 text-white placeholder:text-zinc-600 focus-visible:ring-[#D4C3A3] focus-visible:ring-offset-0";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Reset failed"));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#050505] text-zinc-100 noise-overlay px-6 relative overflow-hidden">
      <div className="absolute top-[-8rem] left-1/2 -translate-x-1/2 h-[32rem] w-[40rem] max-w-full bg-[radial-gradient(ellipse_at_top,rgba(212,195,163,0.12),transparent_60%)] pointer-events-none" />
      <div className="w-full max-w-sm animate-fade-up relative">
        <div className="flex items-center gap-2.5 mb-8 justify-center">
          <img src="/brand-icon-gold.png" alt="Receipt Republic" className="h-10 w-10 object-contain rounded-lg" />
          <span className="font-display font-extrabold text-xl tracking-tight text-white">Receipt Republic</span>
        </div>

        {!token ? (
          <p className="text-center text-zinc-400">Invalid or missing reset link.</p>
        ) : done ? (
          <div className="text-center space-y-4" data-testid="reset-success">
            <CheckCircle2 className="h-12 w-12 text-[#D4C3A3] mx-auto" />
            <p className="font-display font-bold text-lg text-white">Password updated</p>
            <Button data-testid="go-signin-button" onClick={() => navigate("/login")} className="rounded-xl bg-[#D4C3A3] text-black hover:bg-[#E5D6B8] font-semibold">
              Back to sign in
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
            <h1 className="font-display text-2xl font-extrabold tracking-tighter text-center mb-2 text-white">Set a new password</h1>
            <div className="space-y-2">
              <Label className="text-xs font-semibold text-zinc-300">New password</Label>
              <Input data-testid="reset-password-input" type="password" required value={password}
                onChange={(e) => setPassword(e.target.value)} className={inputCls} placeholder="At least 8 characters" />
            </div>
            <Button data-testid="reset-submit-button" type="submit" disabled={busy} className="w-full h-11 rounded-xl bg-[#D4C3A3] text-black hover:bg-[#E5D6B8] font-semibold">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
